// src/pages/BlockedNumbers/BlockedNumbers.jsx
// Tailwind page matching the style used in other forms (e.g., Users, InformativePanel)

import React, { useEffect, useMemo, useState } from 'react';
import BlockedModal from './BlockedModal';
import {
  apiListBlocked,
  apiCreateBlocked,
  apiUpdateBlocked,
  apiDeleteBlocked,
} from './BlockedNumbers.api';

export default function BlockedNumbers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => items, [items]);

  async function fetchData(search = '') {
    setLoading(true);
    setError('');
    try {
      const data = await apiListBlocked(search.trim());
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load list.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchData(''); }, []);

  function fmtDate(v) {
    if (!v) return '—';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  }

  function Badge({ active }) {
    return (
      <span className={`px-2 py-1 rounded text-xs ${active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
        {active ? 'Active' : 'Inactive'}
      </span>
    );
  }

  function openCreate() { setEditing(null); setModalOpen(true); }
  function openEdit(item) { setEditing(item); setModalOpen(true); }

  async function submitModal(payload) {
    if (editing) await apiUpdateBlocked(editing._id, payload);
    else await apiCreateBlocked(payload);
    await fetchData(q);
  }

  async function toggleActive(item) {
    try {
      await apiUpdateBlocked(item._id, { active: !item.active });
      await fetchData(q);
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Failed to update.');
    }
  }

  async function removeItem(item) {
    if (!window.confirm('This will mark the record as inactive (soft delete). Continue?')) return;
    try {
      await apiDeleteBlocked(item._id);
      await fetchData(q);
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Failed to delete.');
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Blocked Numbers</h2>

      {/* Informativo inline no padrão do projeto */}
      <div className="bg-white p-4 rounded-xl shadow mb-4 text-sm text-gray-700">
        Phones here <strong>do not receive automatic AI replies</strong>. Use <strong>E.164</strong> format
        (e.g., <code>+15551234567</code>). Optional temporary block via <em>Expires at</em>. Remove = soft
        delete (<code>active=false</code>).
      </div>

      {/* Barra de ações / busca */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <input
            type="text"
            className="border border-gray-300 rounded px-3 py-2 text-sm w-64"
            placeholder="Search by phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchData(q); }}
          />
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded text-sm"
            onClick={() => fetchData(q)}
          >
            Search
          </button>
          <button
            className="text-sm text-blue-700 underline"
            onClick={() => { setQ(''); fetchData(''); }}
          >
            Reset
          </button>
        </div>

        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm" onClick={openCreate}>
          + Add Blocked Number
        </button>
      </div>

      {/* Tabela */}
      <div className="bg-white shadow rounded-xl overflow-hidden">
        {error && <div className="p-3 text-sm text-red-700 bg-red-100">{error}</div>}
        {loading ? (
          <div className="p-4 text-gray-600">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr className="text-gray-700">
                <th className="p-3 text-left">Phone</th>
                <th className="p-3 text-left">Reason</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Expires</th>
                <th className="p-3 text-left">Created by</th>
                <th className="p-3 text-left">Updated</th>
                <th className="p-3 text-left w-56">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-500">No records.</td>
                </tr>
              ) : filtered.map((item) => (
                <tr key={item._id} className="border-t">
                  <td className="p-3"><code>{item.phone}</code></td>
                  <td className="p-3">{item.reason || '—'}</td>
                  <td className="p-3"><Badge active={item.active} /></td>
                  <td className="p-3">{fmtDate(item.expiresAt)}</td>
                  <td className="p-3">{item.createdBy || '—'}</td>
                  <td className="p-3">{fmtDate(item.updatedAt || item.createdAt)}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button
                        className="px-3 py-1 rounded border border-blue-600 text-blue-600 hover:bg-blue-50"
                        onClick={() => openEdit(item)}
                      >
                        Edit
                      </button>
                      <button
                        className="px-3 py-1 rounded border border-gray-500 text-gray-700 hover:bg-gray-50"
                        onClick={() => toggleActive(item)}
                      >
                        {item.active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="px-3 py-1 rounded border border-red-600 text-red-600 hover:bg-red-50"
                        onClick={() => removeItem(item)}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <BlockedModal
        show={modalOpen}
        onClose={(refresh) => { setModalOpen(false); if (refresh) fetchData(q); }}
        onSubmit={submitModal}
        initial={editing}
      />
    </div>
  );
}
